// react
import { Controller, useFieldArray, useForm } from 'react-hook-form';
import type { Control, FieldPath } from 'react-hook-form';
import { useDispatch } from 'react-redux';

// internationalization
import type { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';

// material ui icons
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import ClearRoundedIcon from '@mui/icons-material/ClearRounded';

// material ui
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  IconButton,
  ListItemText,
  Paper,
  Stack,
  Typography,
} from '@mui/material';

// api
import {
  useAlarmGroupsPostBynewAlarmGroupMutation,
  useAlarmTypesGetCollectionAsyncByqueryOptionsQuery,
} from '@/api/generated/recalc/endpoints/v1/alarms';
import type { ReadAlarmTypeDto } from '@/api/generated/recalc/endpoints/v1/alarms';

// shared components
import { CancelButton } from '@/components/ui/button';
import { SubmitButton } from '@/components/ui/button/SubmitButton';
import { RHFAutocomplete, RHFSelect, RHFTextField } from '@/components/ui/form';

// feature-components
import { SeverityBars } from '@/features/alarm-hub/components/SeverityBars';

// components
import { showSnackbar } from '@/redux/snackbarSlice';

// relative
import {
  DEFAULT_ALARM_GROUP_SUBSCRIPTION,
  toCreateAlarmGroupDto,
  toCreateAlarmGroupFormValues,
} from './createAlarmGroupForm.mappers';
import type { AlarmGroupUserOption } from './createAlarmGroupForm.mappers';
import {
  ALARM_GROUP_SEVERITY_LEVELS,
  createAlarmGroupFormSchema,
} from './createAlarmGroupForm.schema';
import type { CreateAlarmGroupFormValues } from './createAlarmGroupForm.schema';

// libraries
import { zodResolver } from '@hookform/resolvers/zod';

// Alarm type ids currently supported for inclusion in an alarm group. Mirrors
// the list in the legacy AlarmTypeConfigRow.jsx (still used by the edit
// form) - keep both lists in sync until that form is migrated too.
const SUPPORTED_ALARM_TYPE_IDS = [3, 4, 7, 8, 19, 24, 25];

type CreateAlarmGroupFormProps = {
  selectedAccountId?: number;
  userOptions?: AlarmGroupUserOption[];
  onSuccess?: () => void;
  onCancel: () => void;
};

export default function CreateAlarmGroupForm({
  selectedAccountId,
  userOptions = [],
  onSuccess,
  onCancel,
}: CreateAlarmGroupFormProps) {
  const { t } = useTranslation(['alarms', 'forms', 'validation']);
  const dispatch = useDispatch();

  const schema = createAlarmGroupFormSchema(t);
  const {
    control,
    handleSubmit,
    formState: { isValid },
  } = useForm<CreateAlarmGroupFormValues>({
    resolver: zodResolver(schema),
    defaultValues: toCreateAlarmGroupFormValues(),
    mode: 'onTouched',
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'users',
  });

  const [postAlarmGroup, { isLoading: isCreating }] =
    useAlarmGroupsPostBynewAlarmGroupMutation();

  const { data: alarmTypeOptions = [] } =
    useAlarmTypesGetCollectionAsyncByqueryOptionsQuery({
      orderby: 'AlarmTypeId',
      top: 10000,
    });

  const onSubmit = handleSubmit(async (values) => {
    if (!selectedAccountId) {
      return;
    }

    try {
      await postAlarmGroup({
        createAlarmGroupDto: toCreateAlarmGroupDto(
          values,
          selectedAccountId,
          userOptions,
        ),
      }).unwrap();

      dispatch(
        showSnackbar({
          message:
            t('messages.alarm_group_created', { ns: 'alarms' }) ||
            'Alarm group created successfully',
          severity: 'success',
        }),
      );

      onSuccess?.();
    } catch {
      dispatch(
        showSnackbar({
          message:
            t('messages.alarm_group_creation_failed', { ns: 'alarms' }) ||
            'Failed to create alarm group',
          severity: 'error',
        }),
      );
      // Detailed error handling is centralized in apiErrorMiddleware.
    }
  });

  return (
    <form onSubmit={onSubmit} noValidate>
      <Stack spacing={2}>
        <Card variant="outlined">
          <CardContent>
            <Stack spacing={2}>
              <RHFTextField
                name="groupName"
                control={control}
                required
                fullWidth
                label={t('createAlarmGroup.groupName.label', {
                  ns: 'forms',
                })}
                placeholder={t('createAlarmGroup.groupName.placeholder', {
                  ns: 'forms',
                })}
                helperText={t('createAlarmGroup.groupName.helperText', {
                  ns: 'forms',
                })}
              />

              <Box>
                <Box
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                  mb={2}
                >
                  <Typography variant="subtitle1">
                    {t('dialog.users', { ns: 'alarms' })}
                  </Typography>
                  <Button
                    variant="contained"
                    startIcon={<AddRoundedIcon />}
                    onClick={() =>
                      append({ ...DEFAULT_ALARM_GROUP_SUBSCRIPTION })
                    }
                    sx={{ textTransform: 'none', borderRadius: 2 }}
                  >
                    {t('dialog.add_user', { ns: 'alarms' })}
                  </Button>
                </Box>

                {fields.length === 0 ? (
                  <Alert
                    severity="info"
                    sx={{
                      mb: 2,
                      border: '1px solid',
                      borderColor: 'info.main',
                      borderLeft: '4px solid',
                      borderLeftColor: 'info.main',
                    }}
                  >
                    {t('alarmGroups.noUsers', { ns: 'alarms' })}
                  </Alert>
                ) : null}

                <Stack spacing={2}>
                  {fields.map((field, index) => (
                    <AlarmGroupUserRow
                      key={field.id}
                      index={index}
                      control={control}
                      userOptions={userOptions}
                      onRemove={() => remove(index)}
                      t={t}
                    />
                  ))}
                </Stack>
              </Box>

              <AlarmTypeSelectorField
                control={control}
                alarmTypeOptions={alarmTypeOptions}
                t={t}
              />
            </Stack>
          </CardContent>
        </Card>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
          <CancelButton onClick={onCancel} disabled={isCreating}>
            {t('form.cancel', { ns: 'alarms' })}
          </CancelButton>
          <SubmitButton disabled={!isValid || isCreating} loading={isCreating}>
            {t('form.create', { ns: 'alarms' })}
          </SubmitButton>
        </Box>
      </Stack>
    </form>
  );
}

// Inline child: one AlarmSubscriptions row (`users.${index}.*`) for the
// create form's field array. Kept in this file - rather than a separate
// component under `alarm-hub/components/alarm-group/` - so the whole create
// flow stays TypeScript and wired directly to RHF `Controller`s +
// `@/api/generated/**` types.
type AlarmGroupUserRowProps = {
  index: number;
  control: Control<CreateAlarmGroupFormValues>;
  userOptions: AlarmGroupUserOption[];
  onRemove: () => void;
  t: TFunction;
};

function AlarmGroupUserRow({
  index,
  control,
  userOptions,
  onRemove,
  t,
}: AlarmGroupUserRowProps) {
  return (
    <Stack
      direction={{ xs: 'column', sm: 'row' }}
      spacing={2}
      alignItems={{ xs: 'stretch', sm: 'center' }}
    >
      <RHFAutocomplete
        name={`users.${index}.user` as FieldPath<CreateAlarmGroupFormValues>}
        control={control}
        size="small"
        fullWidth
        required
        label={t('createAlarmGroup.user.label', { ns: 'forms' })}
        placeholder={t('createAlarmGroup.user.placeholder', {
          ns: 'forms',
        })}
        helperText={t('createAlarmGroup.user.helperText', { ns: 'forms' })}
        options={userOptions.map((option) => option.User?.Email ?? '')}
        getOptionLabel={(option) => option}
      />
      <RHFSelect
        name={
          `users.${index}.feedSeverity` as FieldPath<CreateAlarmGroupFormValues>
        }
        control={control}
        label={t('createAlarmGroup.feedSeverity.label', { ns: 'forms' })}
        helperText={t('createAlarmGroup.feedSeverity.helperText', {
          ns: 'forms',
        })}
        options={ALARM_GROUP_SEVERITY_LEVELS.map((level) => ({
          value: level,
          label: t(`severity.${level.toLowerCase()}`, { ns: 'alarms' }),
          icon: <SeverityBars severity={level} label="" showLabel={false} />,
        }))}
      />
      <RHFSelect
        name={
          `users.${index}.emailSeverity` as FieldPath<CreateAlarmGroupFormValues>
        }
        control={control}
        label={t('createAlarmGroup.emailSeverity.label', { ns: 'forms' })}
        helperText={t('createAlarmGroup.emailSeverity.helperText', {
          ns: 'forms',
        })}
        options={ALARM_GROUP_SEVERITY_LEVELS.map((level) => ({
          value: level,
          label: t(`severity.${level.toLowerCase()}`, { ns: 'alarms' }),
          icon: <SeverityBars severity={level} label="" showLabel={false} />,
        }))}
      />
      <IconButton
        onClick={onRemove}
        sx={{
          alignSelf: 'center',
          ml: { sm: 'auto' },
          '&:hover': { color: 'error.main' },
        }}
      >
        <ClearRoundedIcon />
      </IconButton>
    </Stack>
  );
}

// Inline child: the AlarmInclusions checklist, wired to the `alarmTypes:
// number[]` field via a single Controller (same rationale as
// AlarmGroupUserRow above - kept local instead of a shared component).
type AlarmTypeSelectorFieldProps = {
  control: Control<CreateAlarmGroupFormValues>;
  alarmTypeOptions: ReadAlarmTypeDto[];
  t: TFunction;
};

function AlarmTypeSelectorField({
  control,
  alarmTypeOptions,
  t,
}: AlarmTypeSelectorFieldProps) {
  return (
    <Controller
      name="alarmTypes"
      control={control}
      render={({ field }) => {
        const selected = field.value ?? [];

        const handleToggle = (alarmTypeId: number) => {
          const isSelected = selected.includes(alarmTypeId);
          field.onChange(
            isSelected
              ? selected.filter((id) => id !== alarmTypeId)
              : [...selected, alarmTypeId],
          );
        };

        return (
          <Box>
            <Typography variant="subtitle1" mb={1}>
              {t('dialog.alarm_types', { ns: 'alarms' })}
            </Typography>

            <Box
              sx={{
                maxHeight: '40vh',
                overflowY: 'auto',
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
                p: 1,
              }}
            >
              {alarmTypeOptions.map((alarmType) => {
                const isSupported = SUPPORTED_ALARM_TYPE_IDS.includes(
                  alarmType.AlarmTypeId,
                );
                const isSelected = selected.includes(alarmType.AlarmTypeId);

                return (
                  <Paper
                    key={alarmType.AlarmTypeId}
                    variant="outlined"
                    sx={{ mb: 1, opacity: isSupported ? 1 : 0.5 }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        p: 1,
                        cursor: isSupported ? 'pointer' : 'not-allowed',
                        '&:hover': {
                          backgroundColor: isSupported
                            ? 'action.hover'
                            : 'transparent',
                        },
                      }}
                      onClick={() =>
                        isSupported && handleToggle(alarmType.AlarmTypeId)
                      }
                    >
                      <Checkbox
                        checked={isSelected}
                        disabled={!isSupported}
                        sx={{ mr: 1 }}
                        onClick={(event) => event.stopPropagation()}
                        onChange={() => handleToggle(alarmType.AlarmTypeId)}
                      />

                      <ListItemText
                        primary={
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 1,
                            }}
                          >
                            <Typography variant="body2">
                              {alarmType.Name}
                            </Typography>
                            {!isSupported ? (
                              <Chip
                                label={
                                  t('type.notSupported', { ns: 'alarms' }) ||
                                  'Not supported'
                                }
                                size="small"
                                sx={{ fontSize: '0.7rem', height: 20 }}
                              />
                            ) : null}
                          </Box>
                        }
                        secondary={alarmType.Description}
                      />
                    </Box>
                  </Paper>
                );
              })}
            </Box>
          </Box>
        );
      }}
    />
  );
}
